import { Link } from "react-router-dom";

import type { CharacterPage } from "@/data/character-pages";
import { compendiumSlug } from "@/data/compendium";
import { describeChapterList } from "@/data/key-items";

interface CharacterMorphsProps {
  character: CharacterPage;
  seriesSlug: string | undefined;
  lookUrl: (stem: string) => string | undefined;
}

/** What a character can become, or the forms they take in the story. */
export function CharacterMorphs(
  {
    character,
    seriesSlug,
    lookUrl,
    variations = false,
  }: CharacterMorphsProps & { variations?: boolean },
) {
  const morphs = (character.morphs ?? [])
    .filter((morph) => (morph.kind === "variation") === variations);
  return morphs.length === 0 ? null : (
    <MorphList morphs={morphs} character={character} seriesSlug={seriesSlug} lookUrl={lookUrl} />
  );
}

function MorphList({
  morphs,
  character,
  seriesSlug,
  lookUrl,
}: CharacterMorphsProps & { morphs: NonNullable<CharacterPage["morphs"]> }) {
  return (
        <div className="character-page__morphs">
          <h3 className="book-section-title">
            {morphs.every((morph) => morph.kind === "variation")
              ? "Variations"
              : "Morphs"}
          </h3>
          {morphs.map((morph) => {
            const image = lookUrl(`${character.id}--${morph.lookId}`);
            return (
              <div key={morph.lookId} className="character-page__morph">
                {image === undefined
                  ? <span className="character-page__morph-image" aria-hidden="true" />
                  : <img className="character-page__morph-image" src={image} alt="" />}
                <div>
                  {morph.kind === "variation"
                    ? <strong>{morph.name}</strong>
                    : <Link to={`/${seriesSlug}/morphs/${compendiumSlug(morph.name)}`}><strong>{morph.name}</strong></Link>}
                  <p className="character-page__fact">{morph.description}</p>
                  {morph.kind === "variation" ? null : (
                    <p className="character-page__from">
                      Acquired: {morph.acquired === null
                        ? "Unknown"
                        : describeChapterList([morph.acquired])}
                    </p>
                  )}
                  <p className="character-page__from">
                    {morph.kind === "variation" ? "Seen in" : "Used in"} {morph.usedIn.length === 0
                      ? "no chapters yet"
                      : describeChapterList(morph.usedIn)}
                  </p>
                  {morph.basedOn === undefined ? null : (
                    <p className="character-page__from">
                      {morph.kind === "variation" ? "With" : "Based on"}{" "}
                      <Link to={`/${seriesSlug}/characters/${morph.basedOn.id}`}>
                        {morph.basedOn.displayName}
                      </Link>
                    </p>
                  )}
                  {morph.imageCredit === undefined ? null : (
                    <p className="character-page__from">{morph.imageCredit}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>);
}
